import { createContext, useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { IS_PAY_ANYONE_P2P } from 'consts/appConsts';
import { baseRoutes, getUserReviewGroups, p2pRoutes } from 'consts/routeConfig';

import {
  selectEnrollmentRequireEmailVerification,
  selectEnrollmentRequirePhoneVerification,
  selectEnrollmentUserReviewStatus,
  selectIsEnrollmentUserReview,
} from 'selectors/enrollment';
import { selectUserProgress, selectUserReferredBy } from 'selectors/user';

export const RouteContext = createContext(null);

export default function useRoutes() {
  return useContext(RouteContext);
}

export const RouteContextProvider = ({ children }) => {
  const userProgress = useSelector(selectUserProgress);
  const isUserReviewState = useSelector(selectIsEnrollmentUserReview);
  const userReviewState = useSelector(selectEnrollmentUserReviewStatus);
  const userReferredBy = useSelector(selectUserReferredBy);
  const isReferredUser = !!userReferredBy;
  const requirePhoneVerification = useSelector(
    selectEnrollmentRequirePhoneVerification
  );
  const requireEmailVerification = useSelector(
    selectEnrollmentRequireEmailVerification
  );

  const activeRouteFlowRef = useRef(
    getActiveRouteFlow({
      isUserReviewState,
      userReviewState,
    })
  );

  const [userProgressIndex, setUserProgressIndex] =
    useState(getUserProgressIndex);
  const [, setActiveRouteFlowRefState] = useState();

  useEffect(() => {
    activeRouteFlowRef.current = getActiveRouteFlow({
      isUserReviewState,
      userReviewState,
      isReferredUser,
    });

    setActiveRouteFlowRefState(activeRouteFlowRef.current);

    setUserProgressIndex(getUserProgressIndex());
  }, [
    isUserReviewState,
    isReferredUser,
    userProgress,
    requirePhoneVerification,
    requireEmailVerification,
  ]);

  function getUserProgressIndex() {
    return (userProgress && getIndexOfPath(userProgress)) || 1;
  }

  function getActiveRouteFlow({ isUserReviewState, userReviewState }) {
    let routeFlow = baseRoutes;

    if (IS_PAY_ANYONE_P2P) {
      routeFlow = p2pRoutes;
    }
    // use this to inject review pages
    routeFlow = combineWithExtraRoute(routeFlow, {
      isUserReviewState,
      userReviewState,
    });
    return routeFlow.map((route, index) => ({
      ...route,
      default: index === 0,
      index: index + 1, // routes are 1-based
    }));
  }

  function getNextPathFromCurrentPath(routePath) {
    const activeRouteFlow = activeRouteFlowRef.current;
    const indexOfNextPath = getIndexOfPath(routePath);
    if (indexOfNextPath < activeRouteFlow.length) {
      return activeRouteFlow[indexOfNextPath].path;
    }
    return null;
  }

  function getSuccessRoute(routeIndex) {
    if (!routeIndex) {
      return null;
    }

    const activeRouteConfig = activeRouteFlowRef.current;
    // Route indices are 1-based instead of 0-based, so we shouldn't increment them
    // when accessing the next route from the active config
    const nextRoute =
      routeIndex < activeRouteConfig.length
        ? activeRouteConfig[routeIndex]
        : null;

    return nextRoute ? nextRoute.path : null;
  }

  /**
   * Determines whether this is a submit page that should
   * trigger an attempt of enrollment
   *
   * @param {number} stepIndex
   */
  function isSubmitPage(stepIndex) {
    if (!stepIndex || stepIndex < 0) {
      return false;
    }

    const routeFlowMap = activeRouteFlowRef.current;

    if (!routeFlowMap) {
      return false;
    }

    return (
      !!routeFlowMap.find(route => route.index === stepIndex)?.isSubmitPage ||
      false
    );
  }

  /**
   * Get the index of the given route path in the current route flow
   *
   * @param {object} routePath    The URL path. ex. '/account'
   */
  function getIndexOfPath(routePath) {
    if (typeof routePath !== 'string') {
      return null;
    }

    const routeFlowMap = activeRouteFlowRef.current;
    /** the routePath should always be in the format of '/account', but we don't
     * set it consistently through the app. That should be fixed in the future,
     * but this formatting catches and fixes it in the case that it's incorrectly formatted. */
    const reformattedRoutePath =
      routePath[0] === '/' ? routePath : `/${routePath}`;

    const matchingRoute =
      routeFlowMap &&
      routeFlowMap.find(route => route.path === reformattedRoutePath);
    return matchingRoute && matchingRoute.index;
  }

  /**
   * Get the index of the given route in the current route flow
   *
   * @param {object} routeObject    The route object as defined in the routeConsts file,
   *                                for which we want the index in the current route flow
   */
  function getIndexOfRoute(routeObject) {
    if (!routeObject) {
      return null;
    }

    return getIndexOfPath(routeObject.path);
  }

  function combineWithExtraRoute(
    routeConfig,
    { isUserReviewState, userReviewState }
  ) {
    let newRouteConfig = [...routeConfig];

    if (isUserReviewState) {
      const reviewRoutes = getUserReviewGroups(userReviewState);
      newRouteConfig = newRouteConfig.concat(reviewRoutes);
    }

    return newRouteConfig;
  }

  function getFirstPathFromRouteFlow() {
    return activeRouteFlowRef.current[0].path;
  }
  const routingData = {
    activeRouteFlow: activeRouteFlowRef.current,
    isSubmitPage,
    getSuccessRoute,
    getIndexOfPath,
    getNextPathFromCurrentPath,
    getIndexOfRoute,
    userProgressIndex,
    getFirstPathFromRouteFlow,
  };

  return (
    <RouteContext.Provider value={routingData}>
      {children}
    </RouteContext.Provider>
  );
};

RouteContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
